// src/services/userService.js
// Centralized User directory + auth/email flows

import api from '../api/axios';

/* ------------------------------- constants ------------------------------- */

export const MIN_SEARCH_CHARS = 2;
export const DEFAULT_LIMIT = 25;

const JUST_LOGGED_OUT_FLAG = 'auth:justLoggedOut';

/* ------------------------------ small utils ------------------------------ */

export function markJustLoggedOut() {
  try {
    sessionStorage.setItem(JUST_LOGGED_OUT_FLAG, '1');
  } catch {}
}

export function clearJustLoggedOut() {
  try {
    sessionStorage.removeItem(JUST_LOGGED_OUT_FLAG);
  } catch {}
}

export function isJustLoggedOut() {
  try {
    return sessionStorage.getItem(JUST_LOGGED_OUT_FLAG) === '1';
  } catch {
    return false;
  }
}

export function clearAuthCaches() {
  try {
    localStorage.removeItem('token'); // dev (bearer) only
    localStorage.removeItem('user');
    localStorage.removeItem('me');
  } catch {}
  try {
    // ensure no lingering bearer header survives a SPA reload
    if (api?.defaults?.headers?.common) {
      delete api.defaults.headers.common.Authorization;
    }
  } catch {}
}

/* -------------------------------- mappers -------------------------------- */

export const toUserDTO = (u = {}) => ({
  id: String(u.id || u._id || ''),
  name:
    u.name ||
    u.fullName ||
    u.displayName ||
    (u.email ? u.email.split('@')[0] : 'User'),
  email: u.email || '',
  role: u.role || 'staff',
  avatarUrl: u.avatarUrl || u.avatar || null,
  active: u.active !== false && u.softDeleted !== true,
  invited: !!u.invited,
  invitedAt: u.invitedAt ? new Date(u.invitedAt) : null,
  emailVerifiedAt: u.emailVerifiedAt ? new Date(u.emailVerifiedAt) : null,
  isVerified: !!u.emailVerifiedAt,
  lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : null,
});

export const toUserOption = (u = {}) => ({
  value: String(u.id || u._id || ''),
  label: u.name || (u.email ? u.email.split('@')[0] : 'User'),
  avatarUrl: u.avatarUrl || null,
  meta: toUserDTO(u),
});

const extractArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.users)) return d.users;
  return [];
};

/* ---------------------------------- API ---------------------------------- */

/**
 * GET /users
 * Server expects: { search, role, includeInactive, includeInvited, page, limit }
 * NOTE: We default includeInvited=true to avoid empty lists on fresh tenants.
 */
export async function listUsers(
  {
    search = '',
    role,
    includeInactive = false,
    includeInvited = true,
    page = 1,
    limit = DEFAULT_LIMIT,
  } = {},
  { signal } = {}
) {
  const params = {
    search: String(search || '').trim(),
    role,
    includeInactive: includeInactive ? 1 : 0,
    includeInvited: includeInvited ? 1 : 0,
    page,
    limit,
  };
  const res = await api.get('/users', { params, signal });
  return extractArray(res).map(toUserDTO);
}

/**
 * GET /users/by-ids?ids=...
 */
export async function getUsersByIds(ids = [], { signal } = {}) {
  const uniq = Array.from(new Set((ids || []).map(String))).filter(Boolean);
  if (!uniq.length) return [];
  const res = await api.get('/users/by-ids', {
    params: { ids: uniq.join(',') },
    signal,
  });
  return extractArray(res).map(toUserDTO);
}

/**
 * POST /users/invite
 */
export async function inviteUser(payload, { signal } = {}) {
  const res = await api.post('/users/invite', payload, { signal });
  return res.data;
}

/**
 * PATCH /users/:id/role
 */
export async function updateUserRole({ id, role }, { signal } = {}) {
  const res = await api.patch(`/users/${id}/role`, { role }, { signal });
  return res.data;
}

/**
 * DELETE /users/:id
 */
export async function deleteUser(id, { signal } = {}) {
  const res = await api.delete(`/users/${id}`, { signal });
  return res.data;
}

/* -------------------------- Auth / email workflows ------------------------ */

/**
 * POST /auth/register
 * Thin wrapper – payload should already include promoCode if present.
 */
export async function register(payload, { signal } = {}) {
  const res = await api.post('/auth/register', payload, { signal });
  return res.data; // { token?, checkoutUrl?, message? }
}

/**
 * POST /auth/request-reset (neutral success)
 */
export async function requestReset(email, { signal } = {}) {
  const res = await api.post('/auth/request-reset', { email }, { signal });
  return res.data; // { msg }
}

/**
 * POST /auth/reset
 */
export async function resetPassword(
  { token, newPassword, confirmPassword },
  { signal } = {}
) {
  const res = await api.post(
    '/auth/reset',
    { token, newPassword, confirmPassword },
    { signal }
  );
  return res.data; // { msg }
}

/**
 * POST /auth/send-verify
 * If no email is provided, send *no body* to avoid body-parser errors.
 */
export async function sendVerify({ email } = {}, { signal } = {}) {
  const body = email ? { email } : undefined; // <-- was null
  const res = await api.post('/auth/send-verify', body, { signal });
  return res.data; // neutral { msg }
}

/**
 * GET /auth/verify-email/:token  (path-style)
 */
export async function verifyEmail(token, { signal } = {}) {
  const res = await api.get(
    `/auth/verify-email/${encodeURIComponent(token)}`,
    { signal }
  );
  return res.data; // { msg }
}

/**
 * POST /users/invite/accept
 */
export async function acceptInvite(
  { token, name, password, confirmPassword },
  { signal } = {}
) {
  const res = await api.post(
    '/users/invite/accept',
    { token, name, password, confirmPassword },
    { signal }
  );
  return res.data; // { success, user }
}

/* ----------------------------- Admin – invites ---------------------------- */

export async function listInvites(
  { search = '', page = 1, limit = DEFAULT_LIMIT } = {},
  { signal } = {}
) {
  const res = await api.get('/users/invites', {
    params: { search, page, limit },
    signal,
  });
  return res.data; // keep meta (items + pagination)
}

export async function resendInvite(id, { signal } = {}) {
  const res = await api.post(
    `/users/invites/${encodeURIComponent(id)}/resend`,
    undefined,
    { signal }
  ); // <-- was null
  return res.data; // { success, message }
}

export async function cancelInvite(id, { signal } = {}) {
  const res = await api.delete(
    `/users/invites/${encodeURIComponent(id)}`,
    { signal }
  );
  return res.data; // { success, message }
}

/* ------------------------------- Profile API ------------------------------ */

export async function getProfile({ signal } = {}) {
  const res = await api.get('/auth/profile', { signal });
  return res.data; // user object
}

/* ------------------------------- Session API ------------------------------ */
/**
 * POST /auth/logout — clears cookie-session on server.
 * Also marks a "just logged out" flag so interceptors/guards can skip refresh on the next paint,
 * clears local caches, and removes any leftover Authorization header.
 */
export async function logout({ signal } = {}) {
  // set guard first (prevents any in-flight interceptors from reviving the session)
  markJustLoggedOut();

  try {
    await api.post('/auth/logout', undefined, { signal }); // <-- was null
  } finally {
    clearAuthCaches();
  }
}
